import { call, fork, put, select, takeLatest } from 'redux-saga/effects';
import { gcApi } from 'config/axios';

import { ALL_FEED_TYPE } from 'utils/groups/GroupsUtil';

/** redux selector */
import { selectGroup, selectGroupAllFeedsList, selectGroupPosts } from 'reducers/group/groupPage';
import { selectNewsPostUuid, selectImageUuid, selectActivityId } from 'reducers/group/groupPageReportPhotoDialog';

/** redux actions */
import { hideModal } from 'actions';
import {
    deleteGroupPostSuccess,
    removeImageFromGroupActivityFeed,
    removeImageFromGroupPost,
    removeImageFromGroupPostEnd,
    removeImageFromGroupPostStart,
    submitReportGroupPhotoDone
} from '../actions/groupPageActions';

import {
    GROUP_PAGE_CLOSE_REPORT_GROUP_PHOTO_DIALOG,
    GROUP_PAGE_SUBMIT_REPORT_GROUP_PHOTO,
    REMOVE_GROUP_PROFILE_IMAGE,
    REMOVE_IMAGE_FROM_GROUP_POST,
    REMOVE_IMAGE_FROM_GROUP_POST_END
} from '../actions/ActionTypes';

import '../actions/groupPageActionsTypeDefs';

const REMOVE_IMAGE_STATUS = [`FLAGGED_FOR_REVIEW_BY_HUMAN`, `FLAGGED_FOR_SECOND_REVIEW_BY_HUMAN`];

export function* reportActivityFeedImageSaga(action) {
    const { flagReason, note } = action;
    const activityId = yield select(selectActivityId);
    const imageUuid = yield select(selectImageUuid);
    try {
        const response = yield call(gcApi.post, `/activity-service/activity/${activityId}/image/${imageUuid}/flag`, {
            flagReason,
            referrerContext: 'GCWeb Activity',
            note
        });
        yield put(submitReportGroupPhotoDone({ reviewStatus: response.data.reviewStatus }));
        /** only remove image under these status */
        if (REMOVE_IMAGE_STATUS.includes(response.data.reviewStatus)) {
            /** close photo modal */
            yield put(hideModal());
            yield put(removeImageFromGroupActivityFeed({ activityId, imageUuid }));
        }
    } catch (error) {
        if (error.response && error.response.status === 429) {
            yield put(submitReportGroupPhotoDone({ reviewStatus: 'REPORTED' }));
        } else {
            yield put(submitReportGroupPhotoDone({ error: true, reviewStatus: null }));
        }
    }
}

export function* reportGroupPostImageSaga(action) {
    const { flagReason, note } = action;
    const newsPostUuid = yield select(selectNewsPostUuid);
    const imageUuid = yield select(selectImageUuid);

    try {
        const response = yield call(
            gcApi.post,
            `/conversation-service/newspost/${newsPostUuid}/image/${imageUuid}/flag`,
            {
                flagReason,
                referrerContext: 'GCWeb Group Post',
                note
            }
        );
        yield put(submitReportGroupPhotoDone({ reviewStatus: response.data.reviewStatus }));
        /** only remove image under these status */
        if (REMOVE_IMAGE_STATUS.includes(response.data.reviewStatus)) {
            /** close photo modal */
            yield put(hideModal());
            yield put(removeImageFromGroupPost({ postUuid: newsPostUuid, imageUuid }));
        }
    } catch (error) {
        if (error.response && error.response.status === 429) {
            yield put(submitReportGroupPhotoDone({ reviewStatus: 'REPORTED' }));
        } else {
            yield put(submitReportGroupPhotoDone({ error: true, reviewStatus: null }));
        }
    }
}

export function* reportGroupProfileSaga(action) {
    const { flagReason, note } = action;
    const group = yield select(selectGroup);
    try {
        const response = yield call(gcApi.post, `/group-service/group/${group.id}/image/flag`, {
            flagReason,
            referrerContext: 'GCWeb Group Profile',
            note
        });
        yield put(submitReportGroupPhotoDone({ reviewStatus: response.data.reviewStatus }));
    } catch (error) {
        if (error.response && error.response.status === 429) {
            yield put(submitReportGroupPhotoDone({ reviewStatus: 'REPORTED' }));
        } else {
            yield put(submitReportGroupPhotoDone({ error: true, reviewStatus: null }));
        }
    }
}

export function* submitReportGroupPhotoSaga(action) {
    const activityId = yield select(selectActivityId);
    const newsPostUuid = yield select(selectNewsPostUuid);

    if (activityId) {
        yield fork(reportActivityFeedImageSaga, action);
        return;
    }

    if (newsPostUuid) {
        yield fork(reportGroupPostImageSaga, action);
        return;
    }

    yield fork(reportGroupProfileSaga, action);
}

export function* removeImageFromGroupPostSaga(action) {
    const { postUuid, imageUuid } = action;

    yield put(removeImageFromGroupPostStart({ postUuid, imageUuid }));
    yield put(removeImageFromGroupPostEnd({ postUuid }));
}

export function* removeImageFromGroupPostEndSaga(action) {
    const { postUuid } = action;

    const groupPostsState = yield select(selectGroupPosts);
    const groupPostsList = groupPostsState.list;

    const groupPost = groupPostsList.find((item) => item.uuid === postUuid);

    if (groupPost && !groupPost.body && (groupPost.postImages || []).length === 0) {
        yield put(deleteGroupPostSuccess({ postUuid }));
    }

    const allFeedsList = yield select(selectGroupAllFeedsList);

    const foundFeed = allFeedsList.find((item) => {
        if (item.groupFeedType !== ALL_FEED_TYPE.post) {
            return false;
        }
        return item.data.uuid === postUuid;
    });

    const postInAllFeed = foundFeed && foundFeed.data;
    if (postInAllFeed && !postInAllFeed.body && (postInAllFeed.postImages || []).length === 0) {
        yield put(deleteGroupPostSuccess({ postUuid }));
    }
}

export function* groupPageCloseReportGroupPhotoDialogSaga(action) {
    const { isGroupProfile, reviewStatus } = action;
    /** only remove image under these status */
    if (isGroupProfile && REMOVE_IMAGE_STATUS.includes(reviewStatus)) {
        yield put({ type: REMOVE_GROUP_PROFILE_IMAGE });
    }
}

// watcher
export const groupPageReportPhotoDialogSagas = [
    takeLatest(GROUP_PAGE_SUBMIT_REPORT_GROUP_PHOTO, submitReportGroupPhotoSaga),
    takeLatest(REMOVE_IMAGE_FROM_GROUP_POST, removeImageFromGroupPostSaga),
    takeLatest(REMOVE_IMAGE_FROM_GROUP_POST_END, removeImageFromGroupPostEndSaga),
    takeLatest(GROUP_PAGE_CLOSE_REPORT_GROUP_PHOTO_DIALOG, groupPageCloseReportGroupPhotoDialogSaga)
];
